import {Category} from '@/apiTypes/event';
import {CategoryItem} from '@/components/eventRoutes/categories/categoryItem';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
} from '@/components/ui/select';
import {Typography} from '@/components/ui/typography';
import {FC} from 'react';

interface CategoryDropdownProps {
  categories: Category[];
  activeCategory: Category | null;
  onSelect: (categoryId: string) => void;
  /** Placeholder shown when no category is selected. Defaults to "Select a category". */
  placeholder?: string;
  /** Id of the element that visually labels this dropdown, used for `aria-labelledby`. */
  'aria-labelledby'?: string;
}

/**
 * Bare-bones dropdown selector for event categories. Used by the
 * registration sidebar when the selected route has more than three
 * categories — tighter surface than the button list used at ≤ 3.
 */
export const CategoryDropdown: FC<CategoryDropdownProps> = ({
  categories,
  activeCategory,
  onSelect,
  placeholder = 'Select a category',
  'aria-labelledby': ariaLabelledBy,
}) => {
  return (
    <Select
      value={activeCategory?.id ?? null}
      onValueChange={(value) => {
        if (typeof value === 'string') onSelect(value);
      }}
    >
      <SelectTrigger
        className="h-auto p-3 items-center"
        aria-labelledby={ariaLabelledBy}
      >
        {activeCategory ? (
          <div className="min-w-0 flex-1">
            <CategoryItem category={activeCategory} />
          </div>
        ) : (
          <Typography level="B4" className="text-neutral-500">
            {placeholder}
          </Typography>
        )}
      </SelectTrigger>
      <SelectPopup>
        {categories.map((category) => (
          <SelectItem
            key={category.id}
            value={category.id}
            className="py-3 items-start"
            disabled={!category.isOpen || category.isSoldOut}
          >
            <CategoryItem category={category} />
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
};
